import { registerSW } from "virtual:pwa-register";

import { drainOutbox } from "./lib/offline/offlineSync";
import { autoPullReplica } from "./lib/offline/offlinePull";
import { isPublicAuthPath } from "./lib/publicAuthPath";

export type PwaBlockReason = "offline" | "sync-failed";

type BlockListener = (reason: PwaBlockReason | null) => void;

const listeners = new Set<BlockListener>();

export function subscribePwaBlock(listener: BlockListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify(reason: PwaBlockReason | null): void {
  listeners.forEach((listener) => listener(reason));
}

async function applyUpdateWhenSynced(updateSW: (reload?: boolean) => Promise<void>): Promise<void> {
  if (!navigator.onLine) {
    notify("offline");
    return;
  }
  const result = await drainOutbox();
  if (result.failed > 0 || result.needs_password > 0) {
    notify("sync-failed");
    return;
  }
  notify(null);
  await updateSW(true);
}

/** Workbox: najpierw sync IndexedDB, potem skipWaiting. */
export function registerPwa(): void {
  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      void applyUpdateWhenSynced(updateSW);
    },
  });
}

/**
 * Auto-pull replica przy starcie aplikacji.
 * Wywołać z main.tsx po załadowaniu.
 */
export async function initOffline(): Promise<void> {
  if (isPublicAuthPath()) {
    return;
  }

  // Auto-pull w tle (jitter rozprasza reconnect wielu klientów)
  void autoPullReplica({ jitter: true, trigger: "startup" });

  // Drain outbox jeśli online
  if (navigator.onLine) {
    void drainOutbox();
  }

  // Nasłuchuj online/offline
  window.addEventListener("online", () => {
    if (isPublicAuthPath()) {
      return;
    }
    console.log("[pwa] Online - starting drain and auto-pull");
    void drainOutbox();
    void autoPullReplica({ jitter: true, trigger: "online" });
  });
}
